import { index, pgEnum, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { usStatesEnum, usStatesEnumSchema } from "./commonSchema";
import { patientsTable } from "./patientsSchema";
import { customDate, customTimestamp } from "./utils/types";

export const workRequirementScreeningStatusEnumSchema = z.enum([
  "NOT_STARTED",
  "IN_PROGRESS",
  "COMPLETED",
]);
export type WorkRequirementScreeningStatusEnumType = z.infer<
  typeof workRequirementScreeningStatusEnumSchema
>;
export const workRequirementScreeningStatusEnum = pgEnum(
  "work_requirement_screening_status_enum",
  workRequirementScreeningStatusEnumSchema.enum,
);

export const workRequirementScreeningStepEnumSchema = z.enum([
  "WHO",
  "EXEMPTIONS",
  "INCOME",
  "HOURS",
  "CASE_NUMBERS",
  "NEXT_STEPS",
]);
export type WorkRequirementScreeningStepEnumType = z.infer<
  typeof workRequirementScreeningStepEnumSchema
>;
export const workRequirementScreeningStepEnum = pgEnum(
  "work_requirement_screening_step_enum",
  workRequirementScreeningStepEnumSchema.enum,
);

export const workRequirementsTable = pgTable(
  "work_requirements",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    eventId: text("event_id").notNull().unique(),
    memberId: text("member_id").notNull(),
    // Nullable: an event can arrive before its patient exists (holding record).
    patientId: uuid("patient_id").references(() => patientsTable.id, {
      onDelete: "cascade",
    }),
    workspaceId: text("workspace_id").notNull(),
    workRequirementDate: customDate("work_requirement_date").notNull(),
    usState: usStatesEnum("us_state").notNull(),
    screeningStatus: workRequirementScreeningStatusEnum("screening_status")
      .notNull()
      .default("NOT_STARTED"),
    screeningLastStep: workRequirementScreeningStepEnum("screening_last_step"),
    screenedAt: customTimestamp("screened_at"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("work_requirements_member_id_index").on(table.memberId),
    index("work_requirements_patient_id_index").on(table.patientId),
  ],
);

export const insertWorkRequirementSchema = createInsertSchema(
  workRequirementsTable,
  {
    eventId: z.string().min(1),
    memberId: z.string().min(1),
    patientId: z.uuid().nullish(),
    workspaceId: z.string().min(1),
    workRequirementDate: z.iso.date(),
    usState: usStatesEnumSchema,
    screeningStatus: workRequirementScreeningStatusEnumSchema.optional(),
    screeningLastStep: workRequirementScreeningStepEnumSchema.nullish(),
    screenedAt: z.iso.datetime().nullish(),
  },
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertWorkRequirementType = z.infer<
  typeof insertWorkRequirementSchema
>;

export const workRequirementSchema = createSelectSchema(workRequirementsTable, {
  usState: usStatesEnumSchema,
  screeningStatus: workRequirementScreeningStatusEnumSchema,
  screeningLastStep: workRequirementScreeningStepEnumSchema.nullable(),
});
export type WorkRequirementType = z.infer<typeof workRequirementSchema>;
